import { Button, ButtonsGroup, notify, Space, Text } from '@billy/design-system'
import { ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useOpenBankingAccounts } from '@features/openBanking/hooks/useOpenBankingAccounts'
import { useSyncOpenBankingConnection } from '@features/openBanking/hooks/useSyncOpenBankingConnection'

import { BankReconciliationSafe } from '../../../../../../../../assets/images'
import { NotificationKeys } from '../../../../../../../../enums/notificationKeys'
import { useSegment } from '../../../../../../../../hooks'
import { useBankLines, usePaymentModal } from '../../../../../../../paymentReconciliationModal/contexts'
import { BankLinesState } from '../../../../../../enums'
import * as Styled from '../styles'

export const RenewBankConnection = (): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { openBankingAccounts } = useOpenBankingAccounts()
  const { syncOpenBankingAccountConnection: renewConnection } = useSyncOpenBankingConnection({
    onSuccess: () => {
      notify({
        id: NotificationKeys.OpenBankingConnectionRenewed,
        variant: 'success',
        message: t('bank_integration.connection_renewal_success_message'),
      })
      setBankLinesState(BankLinesState.BankLinesList)
    },
    onError: () => {
      notify({
        id: NotificationKeys.OpenBankingConnectionRenewed,
        variant: 'error',
        message: t('bank_integration.generic_error_message'),
      })
    },
  })
  const { selectedAccountId } = usePaymentModal()
  const { bankLines, setBankLinesState } = useBankLines()

  const handleRenewConnectionButtonClick = useCallback(() => {
    track('xxx - expenses - Bank line modal - renew bank connection button clicked')

    if (!selectedAccountId) {
      return
    }

    const connectedBankId = openBankingAccounts?.find(
      (bankConnection) => bankConnection?.externalAccountId === selectedAccountId,
    )?.id

    if (connectedBankId) {
      renewConnection(connectedBankId)
    }
  }, [renewConnection, selectedAccountId, openBankingAccounts, track])

  const handleSkipButtonClick = useCallback(() => {
    track('xxx - expenses - Bank line modal - skip bank connection button clicked')

    if (!bankLines?.length) {
      setBankLinesState(BankLinesState.Empty)
      return
    }

    setBankLinesState(BankLinesState.BankLinesList)
  }, [bankLines?.length, setBankLinesState, track])

  useEffect(() => {
    track('xxx - expenses - Bank line modal - renew bank connection button shown')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Styled.EmptyStateWrapper>
      <BankReconciliationSafe />
      <Space size="s8" />
      <Text alignment="center">{t('bill.payment_reconcile.your_bank_needs_reconnecting')}</Text>
      <Space />
      <ButtonsGroup>
        <Button onClick={handleSkipButtonClick} variant="text">
          {t('skip')}
        </Button>
        <Button onClick={handleRenewConnectionButtonClick} icon="refresh">
          {t('renew_connection')}
        </Button>
      </ButtonsGroup>
    </Styled.EmptyStateWrapper>
  )
}
